import {
  ChatBubbleOvalLeftEllipsisIcon,
  ClipboardDocumentCheckIcon,
  Cog8ToothIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  PlayCircleIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { CornerUpLeft } from 'lucide-react';
import type React from 'react';

import type { MainTabKey } from '@/sections/poe/types/poe.types';

// Export for use in TabDescription component
export const MAIN_TAB_DEFS: Record<
  MainTabKey,
  {
    id: string;
    label: string;
    heading: string;
    icon: React.ComponentType<{ className?: string }>;
    description: string;
    // When true, the content pane renders no heading/subtitle for this tab.
    hideHeader?: boolean;
  }
> = {
  // ENG-5014: the INFORMATION/"Claim" tab is dissolved — its content is folded
  // into DETECTION at build time (usePOESections `mergeInformationIntoDetection`),
  // so this def is no longer rendered as a rail tab. Kept because the MainTabKey
  // union and the tempTabs['INFORMATION'] push-target still reference the key;
  // per-processor re-homing to Verification/Exploitation lands in ENG-4605 subs.
  INFORMATION: {
    id: 'information',
    label: 'Claim',
    heading: '',
    icon: InformationCircleIcon,
    description: '',
    hideHeader: true,
  },
  REQUEST: {
    id: 'request',
    label: 'Request',
    heading: '',
    icon: ChatBubbleOvalLeftEllipsisIcon,
    description: 'Raw request that triggered the vulnerability detection',
  },
  RESPONSE: {
    id: 'response',
    label: 'Response',
    heading: '',
    icon: CornerUpLeft,
    description: 'Server response confirming the vulnerability',
  },
  DETECTION: {
    id: 'detection',
    label: 'Detection',
    heading: '',
    icon: MagnifyingGlassIcon,
    description: 'How this vulnerability was detected and matched',
  },
  DEMONSTRATED: {
    id: 'demonstrated',
    label: 'Demonstrated',
    heading: 'Demonstrated',
    icon: PlayCircleIcon,
    description: 'Live agent progress and demonstration outcomes',
  },
  ATTRIBUTES: {
    id: 'attributes',
    label: 'Attributes',
    heading: '',
    icon: Cog8ToothIcon,
    description: 'Endpoints and other details attributed to this risk',
  },
  LOGIN_PANEL: {
    id: 'login_panel',
    label: 'Login Panel',
    heading: 'Login Panel',
    icon: ShieldExclamationIcon,
    description: 'A login panel was detected by Chariot',
  },
  REMEDIATION: {
    id: 'remediation',
    label: 'Remediation',
    heading: 'Suggested Remediation',
    icon: CheckCircleIcon,
    description: 'Steps to fix or mitigate this vulnerability',
  },
  IMAGES: {
    id: 'images',
    label: 'Images',
    heading: 'Images',
    icon: PhotoIcon,
    description: 'Screenshots and uploaded images',
  },
  VERIFICATION: {
    id: 'verification',
    label: 'Verification',
    heading: 'Verification',
    icon: ClipboardDocumentCheckIcon,
    // M5: neutral, authorship-agnostic copy. The Verification file
    // (evidence/{risk.key}) is customer-editable and its authorship is not
    // client-derivable, so the description must NOT frame it as Praetorian's
    // independent third-party validation.
    description: 'Validation notes and analysis for this finding',
  },
};
